/**
 * Export model shared by the CSV, XLSX and PDF builders (ADR 0018). A report screen turns its rows into
 * one or more typed sheets; the builders only format. Pure TypeScript (no DOM), so the builders also run
 * in a Web Worker and in Node tests.
 */
import type { ExportFormat, ReportKind } from "@fts/core";

/**
 * Cell types. Values: text → string; int/dec1/dec2 → number; percent → number 0..100; duration → seconds;
 * datetime → ISO instant (shown in the report time zone); date → "YYYY-MM-DD"; bool → boolean.
 */
export type CellType = "text" | "int" | "dec1" | "dec2" | "coord" | "percent" | "duration" | "datetime" | "date" | "bool";
/**
 * Text written by FTS itself (e.g. "+1 operatör (görme yetkiniz yok)"), never by a user: exempt from the
 * CSV formula guard, which is meant for user text (names, notes) only.
 */
export type SystemText = { system: string };
export type Cell = string | number | boolean | null | SystemText;

export const systemText = (s: string): SystemText => ({ system: s });
export const isSystemText = (v: unknown): v is SystemText => typeof v === "object" && v !== null && "system" in v;

export type SheetColumn = { header: string; type: CellType; /** Width in characters (XLSX/PDF hint). */ width?: number };

export type Sheet = { name: string; columns: SheetColumn[]; rows: Cell[][] };

export type ExportLocale = "tr" | "en";

/** Everything a file needs besides the rows. Labels are already translated by the caller. */
export type ExportMeta = {
  report: ReportKind;
  /** "FTS — <report name>" is built from this. */
  reportName: string;
  locale: ExportLocale;
  /** IANA zone the times are shown in. */
  tz: string;
  /** Filter summary as label/value pairs (site, vehicles, dates…). */
  filters: [string, string][];
  generatedAt: string;
  generatedBy: string;
  /** Translated fixed labels used inside the files. */
  labels: {
    filters: string;
    generatedAt: string;
    generatedBy: string;
    timeZone: string;
    page: string; // "Sayfa {page} / {pages}"
    yes: string;
    no: string;
    info: string; // info sheet name
    pdfRowCap: string; // "PDF shows the first {shown} of {total} rows…"
    noRows: string;
    mapUnavailable: string;
  };
};

export type ExportJob = {
  format: ExportFormat;
  meta: ExportMeta;
  sheets: Sheet[];
  /** CSV only: ";" for Turkish Excel (decimal comma), "," otherwise. */
  separator?: "," | ";";
  /** PDF only: PNG/JPEG data URL of the route map. */
  mapImage?: string | null;
  /** PDF only: font files (Noto Sans, OFL). */
  fonts?: { regular: ArrayBuffer; bold: ArrayBuffer };
};

/** Rows per PDF table: a PDF is for reading and printing; the full data goes to CSV/XLSX. */
export const PDF_ROW_CAP = 2000;

/* ---------------- formatting ---------------- */

const pad = (n: number) => String(n).padStart(2, "0");

/** Wall-clock parts of an instant in a time zone. */
export function wallClock(iso: string, tz: string): { y: number; m: number; d: number; hh: number; mm: number; ss: number } | null {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
  }).formatToParts(new Date(t));
  const get = (k: string) => Number(parts.find((p) => p.type === k)?.value ?? "0");
  return { y: get("year"), m: get("month"), d: get("day"), hh: get("hour") % 24, mm: get("minute"), ss: get("second") };
}

export function formatDay(day: string, locale: ExportLocale): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return day;
  return locale === "tr" ? `${m[3]}.${m[2]}.${m[1]}` : `${m[1]}-${m[2]}-${m[3]}`;
}

export function formatDateTimeIn(iso: string, tz: string, locale: ExportLocale, seconds = false): string {
  const w = wallClock(iso, tz);
  if (!w) return iso;
  const date = locale === "tr" ? `${pad(w.d)}.${pad(w.m)}.${w.y}` : `${w.y}-${pad(w.m)}-${pad(w.d)}`;
  return `${date} ${pad(w.hh)}:${pad(w.mm)}${seconds ? `:${pad(w.ss)}` : ""}`;
}

/** "h:mm" (hours may exceed 24). */
export function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.round(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${h}:${pad(m)}`;
}

export function formatNumber(v: number, decimals: number, decimalComma: boolean): string {
  const s = v.toFixed(decimals);
  return decimalComma ? s.replace(".", ",") : s;
}

/** A cell as display text (CSV and PDF). */
export function cellText(v: Cell, type: CellType, meta: Pick<ExportMeta, "tz" | "locale" | "labels">, decimalComma: boolean): string {
  if (v === null || v === undefined || v === "") return "";
  if (isSystemText(v)) return v.system;
  switch (type) {
    case "coord":
      return typeof v === "number" ? formatNumber(v, 6, decimalComma) : String(v);
    case "int":
      return typeof v === "number" ? String(Math.round(v)) : String(v);
    case "dec1":
      return typeof v === "number" ? formatNumber(v, 1, decimalComma) : String(v);
    case "dec2":
      return typeof v === "number" ? formatNumber(v, 2, decimalComma) : String(v);
    case "percent":
      return typeof v === "number" ? formatNumber(v, 1, decimalComma) : String(v);
    case "duration":
      return typeof v === "number" ? formatDuration(v) : String(v);
    case "datetime":
      return typeof v === "string" ? formatDateTimeIn(v, meta.tz, meta.locale) : String(v);
    case "date":
      return typeof v === "string" ? formatDay(v, meta.locale) : String(v);
    case "bool":
      return v === true ? meta.labels.yes : v === false ? meta.labels.no : String(v);
    default:
      return String(v);
  }
}

/** Interpolate {name} placeholders in a label. */
export function fill(template: string, vars: Record<string, string | number>): string {
  return template.replace(/\{(\w+)\}/g, (m, k: string) => (k in vars ? String(vars[k]) : m));
}

/** File name: `fts-<slug>-<part>.<ext>`, ASCII only. */
export function exportFileName(slug: string, part: string, format: ExportFormat): string {
  const clean = (s: string) =>
    s.normalize("NFKD").replace(/[̀-ͯ]/g, "").replace(/ı/g, "i").replace(/[^A-Za-z0-9_-]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "");
  return `fts-${clean(slug)}-${clean(part)}.${format}`;
}

export const MIME: Record<ExportFormat, string> = {
  csv: "text/csv;charset=utf-8",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  pdf: "application/pdf",
};

/** Let the browser breathe between chunks (no-op cost in a worker). */
export const yieldNow = () => new Promise<void>((r) => setTimeout(r, 0));
